import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import EmojiPicker from './components/EmojiPicker';
import MoodCalendar from './components/MoodCalendar';
import { useDailyMoodStore } from './stores/useDailyMoodStore';
import { getLocalDate } from './utils/dateUtils';

import LoadingSpinner from '@/common/components/LoadingSpinner';

export default function DailyMood({ widgetId }: { widgetId: string }) {
  const { t } = useTranslation();
  const { moods, loading, fetchMoods, setMood } = useDailyMoodStore();
  const [selectedEmoji, setSelectedEmoji] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    fetchMoods(widgetId);
  }, [fetchMoods, widgetId]);

  // Set today's mood if it exists
  useEffect(() => {
    const today = getLocalDate();
    const todaysMood = moods.find(mood => mood.date === today);
    if (todaysMood) {
      setSelectedEmoji(todaysMood.emoji);
      setNote(todaysMood.note || '');
    }
  }, [moods]);

  // Auto-save when emoji or note changes
  useEffect(() => {
    if (selectedEmoji) {
      const saveTimeout = setTimeout(() => {
        setMood(widgetId, selectedEmoji, note || undefined);
      }, 500);

      return () => clearTimeout(saveTimeout);
    }
  }, [selectedEmoji, note, widgetId, setMood]);

  return (
    <div className="h-full flex flex-col">
      {loading && <LoadingSpinner />}

      <div className="flex-1 overflow-auto">
        <div className="mx-auto space-y-6">
          <div className="bg-white rounded-2xl px-2 pb-2 shadow-sm">
            <h2 className="text-md font-semibold mb-4">{t('dailyMood.feelingTodayQuestion')}</h2>
            <EmojiPicker
              note={note}
              selected={selectedEmoji}
              onNoteChange={setNote}
              onSelect={setSelectedEmoji}
            />
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('dailyMood.moodPatterns')}</h3>
            <MoodCalendar moods={moods} />
          </div>
        </div>
      </div>

    </div>
  );
}
